import React from "react";
import PropTypes from "prop-types";

/**
 * Prevent "white page" on runtime crashes by showing a fallback UI.
 * Also logs the error so it can be debugged from the console.
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary] Uncaught UI error:", {
      message: error?.message,
      stack: error?.stack,
      componentStack: info?.componentStack,
      error,
      info,
    });
    this.setState({ info });
  }

  render() {
    const { hasError, error, info } = this.state;
    const { children } = this.props;

    if (!hasError) return children;

    const msg = error?.message || String(error || "Unknown error");
    const stack = error?.stack || "";
    const componentStack = info?.componentStack || "";
    const debugText = [
      `Message: ${msg}`,
      stack ? `\nStack:\n${stack}` : "",
      componentStack ? `\nComponent stack:\n${componentStack}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    return (
      <div style={{ padding: 24, fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <h2 style={{ margin: "0 0 8px 0" }}>Something went wrong</h2>
          <p style={{ margin: "0 0 16px 0", color: "#444" }}>
            The app hit an unexpected error and stopped rendering. Try reloading. If it keeps happening, copy the error message
            below and send it to an admin/dev.
          </p>
          <div
            style={{
              padding: 12,
              border: "1px solid #ddd",
              borderRadius: 8,
              background: "#fafafa",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              marginBottom: 16,
            }}
          >
            <strong>Error:</strong> {msg}
          </div>
          <details style={{ marginBottom: 16 }}>
            <summary style={{ cursor: "pointer", fontWeight: 700 }}>Debug details</summary>
            <div
              style={{
                marginTop: 10,
                padding: 12,
                border: "1px solid #eee",
                borderRadius: 8,
                background: "white",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {debugText}
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(debugText);
                  } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn("[AppErrorBoundary] Clipboard copy failed:", e);
                  }
                }}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid #111",
                  background: "white",
                  color: "#111",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Copy debug
              </button>
            </div>
          </details>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #111",
              background: "#111",
              color: "white",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

AppErrorBoundary.propTypes = {
  children: PropTypes.node,
};

